import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "./api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Cookie-based session: probe /auth/me on mount. 401 = no session.
    api
      .get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => setUser(null))
      .finally(() => setLoading(false));
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setUser(data.user);
    return data.user;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      // Server-side logout can fail (network hiccup, session already expired).
      // Log for diagnostics but always clear locally so the UI recovers.
      console.warn("logout: server call failed, clearing session locally", e);
    }
    setUser(null);
  };

  /**
   * Shallow-merge a preference patch into the current auth user.
   *
   * Called by every preference hook (`useTheme`, `useFontScale`,
   * `useLayoutDensity`, `useWonChime`) AFTER a successful PUT to
   * `/users/me/preferences`.  Keeps the auth context in sync so that when
   * a preference card re-mounts (e.g. the partner returns to Settings) it
   * hydrates from the fresh value, not from the stale one captured on
   * initial /auth/me. This is the fix for the "settings appear to reset
   * when I come back to the Settings page" bug.
   *
   * Guard: no-op if no user is signed in.
   */
  const mergePreferences = useCallback((patch) => {
    setUser((prev) => {
      if (!prev) return prev;
      return { ...prev, preferences: { ...(prev.preferences || {}), ...(patch || {}) } };
    });
  }, []);

  return (
    <AuthCtx.Provider value={{ user, loading, login, register, logout, mergePreferences }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
